import React, { useState } from 'react';

const CERT_API = 'https://uqnbqddi2m.execute-api.us-west-1.amazonaws.com/grades/cert';

interface Defect { area: string; description: string; severity: string; }

interface CertResult {
  certNumber: string;
  grade: number;
  label: string;
  title: string;
  issueNumber: string;
  summary: string;
  defects: Defect[];
  gradedAt: string;
  confidence: string;
  signature: boolean;
  photos: string[];
}

export default function CertLookup() {
  const [certInput, setCertInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CertResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  const handleLookup = async () => {
    const cert = certInput.trim().replace(/\D/g, '');
    if (!cert) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setPhotoIndex(0);
    try {
      const resp = await fetch(`${CERT_API}/${cert}`);
      if (resp.status === 404) throw new Error(`No submission found for cert #${cert}`);
      if (!resp.ok) throw new Error(`Lookup failed: ${resp.status}`);
      setResult(await resp.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cert-lookup">
      <h2>Cert # Lookup</h2>
      <p className="cert-lookup-hint">Enter a cert number to pull up the full submission for dispute review.</p>

      <div className="cert-input-row">
        <input
          className="cert-input"
          type="text"
          placeholder="e.g. 260722160804"
          value={certInput}
          onChange={e => setCertInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
          autoFocus
        />
        <button
          className="cert-lookup-btn"
          onClick={handleLookup}
          disabled={!certInput.trim() || loading}
        >
          {loading ? 'Looking up...' : 'Look Up'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {result && (
        <div className="cert-result">
          <div className="cert-result-header">
            <div>
              <h3 className="cert-title">{result.title} #{result.issueNumber}</h3>
              <div className="cert-meta">
                Cert #{result.certNumber}
                <span className="cert-meta-sep">·</span>
                Graded {new Date(result.gradedAt).toLocaleString()}
                {result.signature && <span className="cert-sig-badge">Signed</span>}
              </div>
            </div>
            <div className="cert-grade-block">
              <span className="big-grade">{result.grade.toFixed(1)}</span>
              <span className="big-label">{result.label}</span>
              <span className="confidence-badge">{result.confidence} confidence</span>
            </div>
          </div>

          {result.photos.length > 0 && (
            <div className="photo-panel">
              <div className="photo-main">
                <img
                  src={result.photos[photoIndex]}
                  alt={`Photo ${photoIndex + 1}`}
                  className="main-photo"
                />
                {result.photos.length > 1 && (
                  <>
                    <button
                      className="photo-nav photo-nav-prev"
                      onClick={() => setPhotoIndex(i => Math.max(0, i - 1))}
                      disabled={photoIndex === 0}
                      aria-label="Previous photo"
                    >&#8249;</button>
                    <button
                      className="photo-nav photo-nav-next"
                      onClick={() => setPhotoIndex(i => Math.min(result.photos.length - 1, i + 1))}
                      disabled={photoIndex === result.photos.length - 1}
                      aria-label="Next photo"
                    >&#8250;</button>
                    <div className="photo-counter">{photoIndex + 1} / {result.photos.length}</div>
                  </>
                )}
              </div>
              <div className="photo-strip">
                {result.photos.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`Photo ${i + 1}`}
                    className={`thumb ${i === photoIndex ? 'thumb-active' : ''}`}
                    onClick={() => setPhotoIndex(i)}
                  />
                ))}
              </div>
            </div>
          )}

          {result.summary && (
            <section className="info-section">
              <h3>AI Summary</h3>
              <p className="ai-summary">{result.summary}</p>
            </section>
          )}

          {result.defects?.length > 0 && (
            <section className="info-section">
              <h3>Defects</h3>
              <ul className="defect-list">
                {result.defects.map((d, i) => (
                  <li key={i} className={`defect-item severity-${d.severity}`}>
                    <strong>{d.area}</strong>: {d.description}
                    <span className="severity-tag">{d.severity}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
